// (C) 2018 Jason Leigh - University of Hawaii at Manoa, Laboratory for Advanced Visualization & Applications
// 1st Version: April 21, 2018

//
// To get oriented with the code, start by looking at MAIN below. Then look at checkAnswerCB().
// grade() is the function that does the actual grading of a user's input against the list of possible answers.
// It returns a JSON object whose format can be found in reportGrade(). You can use the information in the JSON object
// to give the user feedback on how well they did on trying to answer the question.
// The function setupCurrentQuestion takes the current question from  g_questionDatabase and prepares it for use to question the user.
//
// Current Version:	Aug 18, 2020
// This version now takes a string answer as input rather than a nested set of arrays.
import React from 'react'

export function CheckAnswer(questionDB, inputStr) {

    let dbAnswer = questionDB.answer;
    const dbQuestion = questionDB.question;

    function hasDiacritics(inputStr) {
        if (inputStr === stripDiacritics(inputStr)) return false;
        else return true;
    }

    function stripDiacritics(inputStr) {
        let newStr = inputStr.slice();
        newStr = newStr.replace(/ā/gi,"a");
        newStr = newStr.replace(/ē/gi,"e");
        newStr = newStr.replace(/ī/gi,"i");
        newStr = newStr.replace(/ō/gi,"o");
        newStr = newStr.replace(/ū/gi,"u");
        newStr = newStr.replace(/ʻ/gi,"");
        newStr = newStr.replace(/'/gi,"");
        newStr = newStr.replace(/`/gi,"");
        return newStr;
    }

    function normalizeDelimiters(inputStr){
        // Replace tabs with spaces
        // Remove extraneous spaces
        return (inputStr.replace(/\t+/g," ").replace(/\s+/g," "));
    }

    function normalizeDiacritics(inputStr){
        return inputStr.replace(/\'/g,"Ê»");
    }

    function removePunctuation(inputStr) {
        return inputStr.replace(/\,/g," ").replace(/\./g," ").replace(/\!/g," ").replace(/\;/g," ").replace(/\:/g," ").replace(/\?/g," ");
    }

    function toArray(inputStr, keepDiacritics) {
        let str = normalizeSentence(inputStr, keepDiacritics);
        return str.trim().split(" ");
    }

    function normalizeSentence(inputStr, keepDiacritics) {
        let str= normalizeDelimiters(
            removePunctuation(
                normalizeDiacritics(
                    normalizeDelimiters(
                        inputStr.toLowerCase()))));
        if (keepDiacritics === false) str = stripDiacritics(str);
        //console.log(">>>"+str);
        return str.trim();
    }

    function findInArray(word, array) {
        for (let i in array){
            if (word == array[i]) return i;
        }
        return -1;
    }

    function getReadableAnswers(arrayOfAnswers) {
        let prettyAnswer = [];

        for (let i in arrayOfAnswers){
            let runningAnswer="";
            let eachAnswer = arrayOfAnswers[i];

            for (let j in eachAnswer) {
                let anAnswer = eachAnswer[j];
                if (Array.isArray(anAnswer)){
                    runningAnswer +="(";
                    for (let k in anAnswer){
                        if (k == anAnswer.length-1)
                            runningAnswer+=anAnswer[k];
                        else
                            runningAnswer+=anAnswer[k]+" or ";
                    }
                    runningAnswer +=") ";

                } else {
                    runningAnswer += anAnswer +" ";
                }
            }
            runningAnswer= runningAnswer.trim();
            runningAnswer+= ', '
            prettyAnswer.push(runningAnswer);
        }

        prettyAnswer[prettyAnswer.length-1] = prettyAnswer[prettyAnswer.length-1].substring(0, prettyAnswer[prettyAnswer.length-1].length-2)

        return prettyAnswer;
    }

    // Calculate how many possible answers there are given all the options.
    function calcPossibleAnswers(oneAnswer){
        let totalanswers=1;
        for (let j in oneAnswer) {
            let anAnswer = oneAnswer[j];
            if (Array.isArray(anAnswer)){
                totalanswers *= anAnswer.length;
            }
        }
        return totalanswers;
    }

    function makeAllPossibleAnswers(arrayOfAnswers) {
        let totalAnswer = [];

        for (let i in arrayOfAnswers){
            let eachAnswer = arrayOfAnswers[i];
            //var numAnswers = this.calcPossibleAnswers(eachAnswer);
            let allPossibleAnswers = [];

            constructAnswer("",eachAnswer,0,allPossibleAnswers);
            // console.log("====>"+allPossibleAnswers);
            totalAnswer = totalAnswer.concat(allPossibleAnswers);
        }
        return totalAnswer;
    }

    function constructAnswer(head, answerSoFar, index, allPossibleAnswers) {
        let front = answerSoFar[index];
        index++;
        if (Array.isArray(front)){
            for (let i in front){
                let newhead = head + " " + front[i];
                constructAnswer(newhead,answerSoFar,index,allPossibleAnswers);
            }
        } else if (typeof front === 'string'){
            let newhead = head+" "+front;
            constructAnswer(newhead, answerSoFar, index, allPossibleAnswers);
        } else if (front == null){
            allPossibleAnswers.push(head);
        }
    }

    function newIntersection(input, anAnswer) {
        let maxCount= 0;
        let maxAnswer="";

        let inputArray = toArray(normalizeSentence(input, false));
        let answerArray = toArray(normalizeSentence(anAnswer, false));

        let count = 0;

        for (let j in answerArray) {

            var found = findInArray(answerArray[j], inputArray);
            if (found != -1) {
                count++;
                inputArray[found]=null;

            }
            if (count > maxCount) {
                maxCount = count;
                maxAnswer = anAnswer;
                let maxAnswerLength = answerArray.length;
            }
        }
        return maxCount;
    }

    function convertStringAnswersToArrayForm(inputStr){
        inputStr = normalizeDelimiters(inputStr).trim();

        // Take the answers and split them into arrays of answers.
        let options = inputStr.split(";");

        let resultArr = [];

        // Now convert each answer sentence into the internal array form described above.
        for (let i in options){

            if (options[i] != null){
                let result = deconstruct(options[i]);
                resultArr.push(result);
            }

        }
        return resultArr;
    }

    function deconstruct(inputStr) {
        let resultArr = [];
        let charCount = 0;

        //console.log("DECON "+inputStr);
        do {
            inputStr=inputStr.trim();

            // Find characters up to the < character.
            let findUpToDelimiter = inputStr.substr(0, inputStr.indexOf('<'));

            //console.log("FIND UP TO DELIM "+findUpToDelimiter);

            // If there is nothing up to the < character then it could either be immediately starting as < or
            // there is no < at all and just a regular word/sentence.
            if (findUpToDelimiter == ""){

                //	console.log("CONSIDERING "+inputStr);

                // If there is Ê»<Ê» then we need to take everything inside the < > pair and make it into an array of strings
                if (inputStr.indexOf('<') != -1) {

                    let findOtherHalf = inputStr.substr(1,inputStr.indexOf('>')-1);

                    //	console.log("OTHER HALF "+findOtherHalf);
                    if (findOtherHalf != null){
                        let arr = findOtherHalf.split('|');
                        //	console.log("GRABBED "+arr);
                        resultArr.push(arr);
                        inputStr = inputStr.substr(inputStr.indexOf('>')+1, inputStr.length-1);
                        //	console.log("REMAINING "+inputStr);
                    }
                } else {
                    // If there is no Ê»<Ê» then itÊ»s a simple string so just add this to the result array.
                    //console.log("HACONSIDERING "+inputStr);
                    resultArr.push(inputStr);
                    break;
                }

            } else {	// else we have a string of words leading up to a Ê»<Ê» character.
                let movePast = findUpToDelimiter.length;
                //console.log("ADDING "+findUpToDelimiter);

                // So add that string of words to the array
                resultArr.push(findUpToDelimiter);

                // And move past that string of words to continue parsing
                inputStr = inputStr.substr(movePast, inputStr.length-1);
                //console.log("WAZ LEFT "+inputStr);
            }

        } while (inputStr.length > 0);
        return resultArr;

    }

    function gradeAuxCorrectWordsCorrectOrder(input, answer, keepDiacritics) {
        let inp = normalizeSentence(input, keepDiacritics);
        let ans = normalizeSentence(answer, keepDiacritics);

        return inp == ans;
    }

    function gradeAuxFindWhenOrderWrong(input, anAnswer) {
        let inputArray = toArray(normalizeSentence(input, false));
        let answerArray = toArray(normalizeSentence(anAnswer, false));

        for (let j in inputArray) {
            if (j >= answerArray.length){
                return j;
            }
            if (inputArray[j] !== answerArray[j]){
                return j;
            }
        }
        return inputArray.length;
    }

    function reportGrade(reportStr, bestAnswerStr) {
        var ba = bestAnswerStr.replace(/\s+/g,' ').trim();
        let result = {
            "report"	: reportStr,
            "closestAnswer" : ba,
            "numberOfWordsInAnswer": ba.trim().split(" ").length
        };
        return result;
    }

    function checkDiacritics(input, answer) {
        let inputArray = toArray(normalizeSentence(input, true));
        let answerArray = toArray(normalizeSentence(answer, true));
        let diacriticList="";
        for (let j in inputArray) {

            if (hasDiacritics(answerArray[j])) {
                if (answerArray[j] != inputArray[j]) {
                    diacriticList = diacriticList+inputArray[j]+" ";
                }
            } else
            if (hasDiacritics(inputArray[j]) && (inputArray[j] != answerArray[j])){
                diacriticList = diacriticList+inputArray[j]+" ";
            }
        }
        return diacriticList.trim();
    }

    function grade(input, answers) {
        let bestAnswer = answers[0];
        let bestWordCount=0;
        let anAnswer;

        // Check to see if you happen to get the answer perfectly by doing a straight string comparison
        for(let i in answers){

            anAnswer = answers[i];
            if (gradeAuxCorrectWordsCorrectOrder(input, anAnswer, true)) {
                return reportGrade("🤙🏽🤙🏽🤙🏽Perfect!🤙🏽🤙🏽🤙🏽",anAnswer);

            }
        }


        // Check to see if you happen to get the answer but didnʻt pay attention to diacritics.
        for(let i in answers){
            anAnswer = answers[i];
            if (gradeAuxCorrectWordsCorrectOrder(input, anAnswer, false)) {
                let listOfDiacriticCorrections = checkDiacritics(input,anAnswer);

                return reportGrade("🤙🏽Almost perfect!🤙🏽 See if you can fix the diacritics for: 👉🏼("+listOfDiacriticCorrections+")👈🏼.", anAnswer); // Perfect but with some diacritic errors
            }
        }

        // Figure out how much of what you inputted matches the first part of each possible answer.
        // This will allow me to narrow down the answer that matches what the user entered.
        let maxResult = 0;
        bestAnswer = answers[0];
        for (let i in answers){
            anAnswer = answers[i];
            let result = gradeAuxFindWhenOrderWrong(input,anAnswer);
            if (result > maxResult) {
                maxResult = result;
                bestAnswer = anAnswer;
            }
        }

        //console.log("MAXRESULT "+maxResult+" "+bestAnswer);
        bestWordCount = maxResult;

        // Find number of correct words used
        for (let i in answers){
            //console.log(">>"+i+" "+bestWordCount+" "+bestAnswer);
            let anAnswer = answers[i];
            let result = newIntersection(input,anAnswer);

            if (bestWordCount < result) {
                bestWordCount = result;
                //bestAnswer = anAnswer;
            }

        }
        // Now one more time, using the BestAnswer find where the word ordering first went out of wack
        // so you can present the final report card to the user.
        let result = gradeAuxFindWhenOrderWrong(input,bestAnswer);


        let response="";

        // Figure out what kind of feedback to give to the user.
        if (input.length == 0){
            response = "😤 Sole! you didn't even try to guess!";
            return reportGrade(response, bestAnswer);
        }
        if (result == input.split(" ").length) {
            response= bestWordCount+" word(s) correct, and in correct order.";
            return reportGrade(response, bestAnswer);
        }
        if ((result == 0) && (bestWordCount == 1)) {
            response = "🥴 1 word correct.";
            return reportGrade(response, bestAnswer);
        }
        if ((result == 0) && (bestWordCount == 0)){
            response = "💩 0 words correct.";
            return reportGrade(response, bestAnswer);
        }
        if (result == 0) {
            response = bestWordCount+" words correct 😀, but you'll need to put them in the right order.";
            return reportGrade(response, bestAnswer);
        }
        if (result == 1)
            response= bestWordCount+" word(s) correct, and the first word is in the right place.";
        else
            response= bestWordCount+" word(s) correct, and first "+result+" words are in correct order.";

        return reportGrade(response, bestAnswer);
    }

    function checkAnswerCB(userInput, answerDB) {
        let allCombinationOfAnswers = makeAllPossibleAnswers(answerDB);
        let result = grade(userInput, allCombinationOfAnswers);
        if(result.report !== "🤙🏽🤙🏽🤙🏽Perfect!🤙🏽🤙🏽🤙🏽") {
            result.report = result.report + " (Hint: there are " +result.numberOfWordsInAnswer+ " words in the answer.)";
        }
        result.closestAnswer = getReadableAnswers(answerDB)
        return result;
    }

    let tmpArray = convertStringAnswersToArrayForm(dbAnswer)
    dbAnswer = tmpArray;
    return checkAnswerCB(inputStr, dbAnswer)
}
